"use client";

import React from "react";
import { Brain, Leaf, ShieldCheck, Compass } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const insightsData = [{
  id: "hosted-3cx",
  tabLabel: "Hosted by 3CX",
  icon: ShieldCheck,
  heading: "Gestión Total por 3CX",
  description: "La solución sin complicaciones. 3CX se encarga de las actualizaciones, la seguridad y el mantenimiento del sistema en su propia infraestructura. Ideal para empresas que buscan máxima fiabilidad sin necesidad de administrar servidores internos."
}, {
  id: "self-hosted",
  tabLabel: "Nube Privada",
  icon: Brain,
  heading: "Autogestionado en la Nube",
  description: "Mantenga el control total desplegando su central en su propia cuenta de AWS, Azure o Google Cloud. Esta opción le permite gestionar sus propias reglas de red y almacenamiento, manteniendo la flexibilidad de la infraestructura escalable."
}, {
  id: "on-premise",
  tabLabel: "On-Premise",
  icon: Leaf,
  heading: "Servidores Locales",
  description: "Para entornos que requieren soberanía de datos absoluta o integración física. Instale 3CX directamente en sus servidores Windows o Linux existentes, o incluso en un dispositivo Raspberry Pi para oficinas pequeñas."
}, {
  id: "hybrid-future",
  tabLabel: "Escalabilidad",
  icon: Compass,
  heading: "Arquitectura Flexible",
  description: "Nuestra tecnología le permite migrar entre modalidades de despliegue a medida que su empresa crece. Comience en la nube y muévase a local, o viceversa, sin perder configuraciones ni interrumpir sus comunicaciones críticas."
}];
export default function DeploymentFlexibility() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="255519" id="deployment-flexibility" className="relative w-full bg-background py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/5 blur-[120px] opacity-50 pointer-events-none mix-blend-overlay" />
      
      <div ref={ref} className={`container mx-auto px-6 max-w-5xl relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-3xl md:text-5xl font-default font-light tracking-tight text-foreground mb-6">
            Flexibilidad de <span className="font-semibold text-primary">Despliegue</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default font-light">
            Elija la infraestructura que mejor se adapte a su modelo de negocio, con la garantía de rendimiento de 3CX.
          </p>
        </div>

        <Tabs defaultValue={insightsData[0].id} className="w-full flex flex-col items-center">
          <TabsList className="flex flex-wrap justify-center h-auto bg-muted p-1.5 rounded-lg mb-12 max-w-full border border-border">
            {insightsData.map((tab, i) => <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="rounded-md px-6 py-3 text-sm md:text-base font-default font-medium transition-all data-[state=active]:bg-card data-[state=active]:shadow-sm data-[state=active]:text-primary">
                {tab.tabLabel}
              </TabsTrigger>)}
          </TabsList>

          <div className="w-full max-w-4xl relative">
            {insightsData.map((tab, i) => <TabsContent key={tab.id} value={tab.id} data-index={i} className="mt-0 outline-none focus-visible:ring-0 data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in-50 data-[state=active]:slide-in-from-bottom-4 duration-500">
                <Card className="border border-border shadow-lg bg-card/50 backdrop-blur-sm overflow-hidden relative rounded-lg">
                  {}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-transparent" />
                  
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-8 p-8 md:p-12">
                    <div className="shrink-0 p-6 bg-accent rounded-lg">
                      <tab.icon className="w-12 h-12 text-primary" strokeWidth={1.5} />
                    </div>
                    
                    <div className="flex-1">
                      <CardHeader className="p-0 mb-4">
                        <CardTitle className="text-2xl md:text-3xl font-default font-bold tracking-tight text-foreground">
                          {tab.heading}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-default">
                          {tab.description}
                        </p>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              </TabsContent>)}
          </div>
        </Tabs>
      </div>
    </section>;
}